import { ReactNode } from "react"
import { Dropdown as AntDropdown } from "antd"
import type { MenuProps, DropDownProps } from "antd"

type Props = {
	className?: string
	items: MenuProps["items"]
	children: ReactNode
	onClick?: MenuProps["onClick"]
	open?: boolean
	onOpenChange?: DropDownProps["onOpenChange"]
	placement?: DropDownProps["placement"]
	overlayStyle?: React.CSSProperties
	overlayClassName?: string
	/**
	 * Render the menu next to its trigger instead of portaling it to `document.body`. Required
	 * inside the Apryse shadow DOM, where a `document.body` portal escapes the shadow root and
	 * loses the injected styles — the same escape hatch `Select` exposes under the same name.
	 */
	renderInPlace?: boolean
	/** AntD defaults to `["hover"]`; pass `["click"]` for menus that should not open on pointer-over. */
	trigger?: DropDownProps["trigger"]
	/**
	 * Fully replace the rendered popup content, bypassing `items`/`menu` entirely (e.g. a swatch
	 * grid rather than a list of rows). The returned node owns its OWN styling — inline styles only,
	 * since a `popupRender`ed node is still subject to the same `renderInPlace`/portal rules as the
	 * rest of the popup, so a shadow-scoped CSS Module class won't reach it if the popup portals to
	 * `document.body` (see ColorSwatchPicker for a worked example). `dropdownRender` is AntD's
	 * deprecated name for the same prop — use `popupRender`.
	 */
	popupRender?: DropDownProps["popupRender"]
}

export const Dropdown = ({
	className = "",
	items,
	children,
	placement = "bottomRight",
	onClick,
	open,
	onOpenChange,
	overlayStyle,
	overlayClassName,
	renderInPlace = false,
	trigger,
	popupRender,
}: Props) => {
	return (
		<div data-testid="dropdown">
			<AntDropdown
				destroyOnHidden
				className={className}
				overlayStyle={overlayStyle}
				overlayClassName={overlayClassName}
				menu={{ items, onClick }}
				placement={placement}
				open={open}
				onOpenChange={onOpenChange}
				trigger={trigger}
				{...(popupRender ? { popupRender } : {})}
				getPopupContainer={renderInPlace ? (triggerNode) => triggerNode.parentElement! : undefined}
			>
				{children}
			</AntDropdown>
		</div>
	)
}
